import { Currency } from '@ps/web-core-enums';
import { canUseDOM, getScrollingElem } from '@ps/web-core-utils';
import { StarsLink } from '@ps/web-shared-ui';
import { differenceInMinutes } from 'date-fns';
import type { ThrottleSettings } from 'lodash';
import { findKey, identity, kebabCase, memoize, pickBy, set, throttle, uniqueId } from 'lodash';
import { deflate, inflate } from 'pako';
import type { ComponentType, ReactNode } from 'react';
import React from 'react';
import { qs } from 'url-parse';

import locales from '@swf/generated/locale-data.json';

import { StatusCode } from '../enums/statusCode';

const DYNAMIC_URL_REG_EX = /^\?:/;

export const renderer = canUseDOM ? 'browser' : 'server';

export const isMacLike = canUseDOM && !!navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i);

export const isIPadOS =
  canUseDOM && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

export const sequence = (prefix?: string): string =>
  `${prefix || ''}${new Date().getTime()}${uniqueId()}`;

export const timeout = async (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const memoizedThrottle = <A, R>(
  func: (arg: A) => R,
  wait?: number,
  options?: ThrottleSettings
) => {
  const memo = memoize((arg: A) => throttle(func, wait, options));
  return (arg: A) => memo(arg)(arg);
};

export const toSignedHex = (num: number) => {
  let result = num;
  if (num < 0) {
    result = 0xffffffff + num + 1;
  }
  return `0x${result.toString(16).toUpperCase()}`;
};

export const getDisplayName = <P extends object>(Comp: ComponentType<P>) =>
  Comp.displayName || Comp.name || 'Component';

export const deprecate = (condition: boolean | string, message?: string) => {
  if ((condition && message) || !message) {
    // eslint-disable-next-line no-console
    console.warn(`WARNING: ${message || condition}`);
  }
};

export const contentLink = (text: ReactNode) => (
  <StarsLink href={`//${text}`} target="_blank" rel="noopener noreferrer">
    {text}
  </StarsLink>
);

export const stringRegExp = (pattern: string, flags = 'g') =>
  new RegExp(pattern.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), flags);

// doesn't work on string enums
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const enumKeys = (obj: Record<string, any>): string[] =>
  Object.keys(obj).filter((key) => typeof obj[key] === 'number');

export const secondsToFormattedTime = (ms: number) => {
  const seconds = ms / 1000;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds / 60) % 60;
  const second = seconds % 60;
  return [hours, minutes, second].map((v) => (v < 10 ? '0' + v : v)).join(':');
};

export const joinPath = (path: (string | number)[]) => `/${path.join('/')}`;

export const splitPath = (path: string) => path.split('/').slice(1);

export const getUrlKeys = (path: string) =>
  path.split('/').filter((element) => DYNAMIC_URL_REG_EX.test(element));

export const buildQuery = (query: object) => qs.stringify(pickBy(query, identity));

const scrollElementToTop = (selectors: string) => {
  const mainElement = canUseDOM && document.querySelector(selectors);
  if (!mainElement) {
    return;
  }

  if (mainElement.scrollTop !== 0) {
    mainElement.scrollTop = 0;
    return;
  }

  const scrollingElement = getScrollingElem();
  if (scrollingElement && scrollingElement.scrollTop !== 0) {
    scrollingElement.scrollTop = 0;
  }
};

export const scrollToTop = () => scrollElementToTop('main section');

export const convertMinutes = (
  amount: number
): { days: number; hours: number; minutes: number } => {
  const days = Math.floor(amount / (24 * 60));
  const hours = Math.floor(amount / 60) % 24;
  const minutes = amount % 60;
  return { days, hours, minutes };
};

export const convertMilliseconds = (
  ms: number
): { days: number; hours: number; minutes: number; seconds: number } => {
  const secs = Math.floor(ms / 1000);
  const minutes = Math.floor(secs / 60);
  return { ...convertMinutes(minutes), seconds: secs % 60 };
};

export const getKeyboardFocusableElements = (
  element: Document | HTMLElement = document
): Element[] =>
  [...element.querySelectorAll('a, button, input, textarea, select, details,[tabindex]')].filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('tabindex') !== '-1'
  );

const localeKeys = Object.keys(locales);
// TODO: remove after locale codes are returned with correct casing
export const getLocaleCode = (key: string) =>
  localeKeys.find((locale) => locale.toLowerCase() === key.toLowerCase());

export const velocityControl = (velocityThreshold: number, displayedAt?: number) =>
  displayedAt ? differenceInMinutes(Date.now(), displayedAt) > velocityThreshold : true;

export const enumCase = (text: string) => kebabCase(text).replace(/-/g, '_').toUpperCase();

export const statusKey = (statusCode: StatusCode) =>
  findKey(StatusCode, (value) => value === statusCode);

export const decodeQuery = (query?: string) => {
  try {
    return JSON.parse(
      inflate(Buffer.from(decodeURIComponent(JSON.stringify(query)), 'base64'), {
        to: 'string'
      })
    );
  } catch {
    return '';
  }
};

export const unflattenObject = (
  item: {
    [key: string]: string | undefined;
  },
  delimiter = '.'
) =>
  Object.keys(item).reduce((res, k) => {
    set(res, k.split(delimiter), item[k]);
    return res;
  }, {});

export const encodeQuery = (query: object) =>
  encodeURIComponent(Buffer.from(deflate(JSON.stringify(query))).toString('base64'));

export const currencyResolver = (value: Currency): Currency | undefined =>
  Object.values(Currency).find((currency) => currency === value);
